import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useShallow } from 'zustand/react/shallow';
import {
  useRouteCreateApi,
  useRouteDeleteApi,
  useRouteListApi,
  useRouteUpdateApi,
} from '@hero/api-hooks';
import { useCurrentUser } from '@hero/hooks-shared';
import { getObjectId } from '@hero/schema';
import type { RedirectProp } from '@hero/web-hooks';
import { PlusIcon, RefreshCcwIcon } from '@hero/web-icons';
import { Button } from '../../../ui/Button';
import { PageBack } from '../../../app/PageBack';
import { PricingSheetBulkBar } from './PricingSheetBulkBar';
import { PricingSheetEmpty } from './PricingSheetEmpty';
import { PricingSheetFilters } from './PricingSheetFilters';
import { PricingSheetHeader } from './PricingSheetHeader';
import type { ScenarioLabels } from './PricingSheetHeader';
import { PricingSheetSkeleton } from './PricingSheetSkeleton';
import { PricingSheetTable } from './PricingSheetTable';
import { isRowSavable, rowToCreatePayload, rowToUpdatePayload } from './mapRoute';
import {
  selectPendingRows,
  selectSavableCount,
  usePricingSheetStore,
} from './store/usePricingSheetStore';
import type { SheetScope, SheetSide } from './types';

/**
 * URL param keys for the pricing sheet's session-scoped persistence.
 * Prefixed with `price` so they can't collide with sibling features in
 * the same route (the CRM page also carries `?tab=` and the drawers
 * sync `?customerId=`, `?contactId=`, etc).
 */
const URL_PARAM = {
  side: 'priceSide',
  customer: 'priceCustomer',
  agreement: 'priceAgreement',
} as const;

/**
 * Pricing sheet shell — owns the I/O (load + save) and lets every visual
 * piece subscribe to the {@link usePricingSheetStore} directly. State no
 * longer flows top-down through props; the page just orchestrates the
 * network round-trips.
 *
 * Load model
 *   - `useRouteListApi` is parameterised by `(side, counterparty)` taken
 *     from the store. When either changes the store also drops selection.
 *   - On every fetch we call `seedFromRoutes(routes)` which wipes any
 *     pending local edits (acceptable for the dum UI; surfaced via the
 *     "Reset changes" button).
 *
 * Save model — Save all
 *   1. `status === 'new'` and savable → `useRouteCreateApi`
 *   2. `status === 'modified'` and savable → `useRouteUpdateApi`
 *   3. `status === 'deleted'` → `useRouteDeleteApi`
 *   Fanned out with `Promise.allSettled` so a single 5xx doesn't take down
 *   the whole batch. Cache invalidation in the api-hook layer triggers a
 *   refetch that reseeds the store via the load effect above.
 */
/**
 * Optional props to embed `PricingSheetPage` inside scoped surfaces
 * (e.g. the per-client workspace Pricing tab). Sensible defaults make
 * the standalone `/more/pricing` route an idiomatic `<PricingSheetPage />`.
 */
export interface PricingSheetPageProps {
  /**
   * Scope to seed the store with on mount when no scope has been
   * persisted yet. Use this to lock the sheet to a specific
   * counterparty (`'direct'` scope with a client's org) or to default
   * the standalone page to `'own'`.
   */
  initialScope?: SheetScope;
  /**
   * Optional initial side (`'sell'` / `'buy'`). When omitted, falls
   * back to the store's persisted side or the default `'sell'`.
   */
  initialSide?: SheetSide;
  /**
   * When true, hide the "this price list is for…" cluster (kind
   * toggle + customer/agreement pickers). The header still renders
   * the sell/buy scenario toggle — surfaces that lock the scope from
   * outside still need to let the user flip between selling and
   * buying rates for the locked counterparty.
   */
  hideScopePicker?: boolean;
  /**
   * Optional label overrides for the sell / buy scenario toggle.
   * Use to make the labels counterparty-aware on scope-locked
   * surfaces (e.g. "Selling price to Acme" / "Buying price from
   * Acme" instead of the generic "My selling rate" / "My buying
   * rate"). See {@link ScenarioLabels}.
   */
  scenarioLabels?: ScenarioLabels;
  /**
   * When true, skip URL persistence for `?priceSide=` / `?priceCustomer=`.
   * Use on surfaces that own their own URL params (the client workspace
   * already syncs `?clientId=` / `?tab=`; pricing params on top would
   * be noise).
   */
  disableUrlSync?: boolean;
  /**
   * When true, the `initialScope` / `initialSide` overrides also fire on
   * subsequent prop changes (and clobber any user picks). Defaults to
   * false — the override is one-shot. Set to true on the per-client
   * Pricing tab so switching between clients re-seeds the scope.
   */
  lockScope?: boolean;
  /**
   * Render a `<PageBack>` above the sheet header. Hidden by default;
   * pass `true` to default to the More screen, or a typed route name /
   * config for a custom destination. See `RedirectProp` for the full
   * shape.
   */
  redirect?: RedirectProp;
}

export function PricingSheetPage({
  initialScope,
  initialSide,
  hideScopePicker = false,
  scenarioLabels,
  disableUrlSync = false,
  lockScope = false,
  redirect,
}: PricingSheetPageProps = {}) {
  const { t } = useTranslation(['m-pricing']);
  const { orgId } = useCurrentUser();

  // Store reads — kept fine-grained so re-renders stay tight. Selectors that
  // return arrays go through `useShallow` so React's getSnapshot stays
  // referentially stable when nothing actually changed (otherwise filter()
  // returns a fresh array each call and the store loops forever).
  const side = usePricingSheetStore(s => s.side);
  const scope = usePricingSheetStore(s => s.scope);
  const rowsLength = usePricingSheetStore(s => s.rows.length);
  const hasSeeded = usePricingSheetStore(s => s.hasSeeded);
  const pendingRows = usePricingSheetStore(useShallow(selectPendingRows));
  const savableCount = usePricingSheetStore(selectSavableCount);
  const addRow = usePricingSheetStore(s => s.addRow);
  const resetChanges = usePricingSheetStore(s => s.resetChanges);
  const seedFromRoutes = usePricingSheetStore(s => s.seedFromRoutes);
  const setSide = usePricingSheetStore(s => s.setSide);
  const setScope = usePricingSheetStore(s => s.setScope);
  const resetSheet = usePricingSheetStore(s => s.reset);

  // -------------------------------------------------------------------------
  // URL persistence — see "URL persistence model" block below
  // -------------------------------------------------------------------------
  // Surfaces that own their own URL params (e.g. the per-client workspace)
  // pass `disableUrlSync` so we don't fight them for the search bar.
  usePricingUrlSync({
    side,
    scope,
    setSide,
    setScope,
    resetSheet,
    disabled: disableUrlSync,
  });

  // -------------------------------------------------------------------------
  // External-scope seed — used by callers that mount the sheet inside a
  // scoped container (e.g. workspace Pricing tab locked to a client). We
  // seed the store *after* mount via `setScope` to keep the sheet's own
  // hydration / cleanup behaviour intact.
  //
  // - `lockScope: false` (default): one-shot seed; subsequent user picks
  //   inside the page persist normally.
  // - `lockScope: true`: re-seed whenever `initialScope` changes (e.g.
  //   the workspace switches to a different client) and never let the
  //   user diverge.
  // -------------------------------------------------------------------------
  const seededRef = useRef(false);
  useEffect(() => {
    if (!initialScope) return;
    if (seededRef.current && !lockScope) return;
    seededRef.current = true;
    setScope(initialScope);
    if (initialSide) setSide(initialSide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialScope, initialSide, lockScope]);

  // -------------------------------------------------------------------------
  // Server fetch — derive `listParams` from the active scope.
  //
  // Mirrors the save-side mapping in `mapRoute.ts` so what the user *sees*
  // (filtered by scope) is exactly what they save into (also scoped). The
  // `enabled: !!orgId` flag below keeps the query idle until we know the
  // current org.
  //
  //   | scope.kind   | filter sent to /route?…                                                  |
  //   | ------------ | ------------------------------------------------------------------------ |
  //   | 'own'        | { <mySide>Id: meId, isOwnRoute: true }                                   |
  //   | 'direct'     | { sellerId, buyerId, isOwnRoute: false }                                 |
  //   | 'agreement'  | { sellerId, buyerId, agreementId, isOwnRoute: false }                    |
  // -------------------------------------------------------------------------
  const listParams = useMemo(() => {
    if (!orgId) return {};
    const meId = getObjectId(orgId);
    switch (scope.kind) {
      case 'own':
        return side === 'sell'
          ? { sellerId: meId, isOwnRoute: true }
          : { buyerId: meId, isOwnRoute: true };
      case 'direct': {
        const otherId = getObjectId(scope.counterparty.id);
        return side === 'sell'
          ? { sellerId: meId, buyerId: otherId, isOwnRoute: false }
          : { sellerId: otherId, buyerId: meId, isOwnRoute: false };
      }
      case 'agreement': {
        const otherId = getObjectId(scope.counterparty.id);
        const agreementId = getObjectId(scope.agreement.id);
        return side === 'sell'
          ? {
              sellerId: meId,
              buyerId: otherId,
              agreementId,
              isOwnRoute: false,
            }
          : {
              sellerId: otherId,
              buyerId: meId,
              agreementId,
              isOwnRoute: false,
            };
      }
    }
  }, [orgId, side, scope]);

  const { data: routes, isLoading } = useRouteListApi(listParams, {
    enabled: !!orgId,
  });

  // Sync the store from React Query. `useInfinitePagination`'s `data` is a
  // memoised array keyed on `query.data`, so this effect only re-fires when
  // the underlying pages object actually changes — first load, refetch after
  // a mutation, or after a scope/side change pulls in a different query key.
  // The `if (routes)` guard skips the brief window where `query.data` is
  // `undefined` for a brand-new key (we never want to seed with a stale `[]`
  // that came from the previous key's memo).
  //
  // We use `useLayoutEffect` (not `useEffect`) so the seed commits in the
  // same paint as the render that introduced the new `routes`. Without it
  // there's a one-render gap where the page sees fresh `routes` but stale
  // `rows = []` (still cleared from `setScope`), which causes the empty
  // state to flash for a frame before the table populates.
  useLayoutEffect(() => {
    if (routes) seedFromRoutes(routes);
  }, [routes, seedFromRoutes]);

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------
  const createApi = useRouteCreateApi();
  const updateApi = useRouteUpdateApi();
  const deleteApi = useRouteDeleteApi();
  const isSaving = createApi.isPending || updateApi.isPending || deleteApi.isPending;

  const pendingCount = pendingRows.length;

  // -------------------------------------------------------------------------
  // Save all
  // -------------------------------------------------------------------------
  const handleSaveAll = async () => {
    if (!orgId) {
      toast.error(
        t('sheet.toast.no_org', {
          defaultValue: 'Cannot save — current user is missing an organisation.',
        }),
      );
      return;
    }

    // Status alone doesn't guarantee a routeId — defend against a misrouted
    // row sneaking onto the wrong endpoint (e.g. a `modified` row that
    // somehow lost its `routeId`).
    const toCreate = pendingRows.filter(
      r => r.status === 'new' && r.routeId === null && isRowSavable(r),
    );
    const toUpdate = pendingRows.filter(
      r => r.status === 'modified' && r.routeId !== null && isRowSavable(r),
    );
    const toDelete = pendingRows.filter(
      r => r.status === 'deleted' && r.routeId !== null,
    );

    if (toCreate.length + toUpdate.length + toDelete.length === 0) {
      toast.info(
        t('sheet.toast.nothing_to_save', {
          defaultValue: 'Nothing to save — fill in the missing fields first.',
        }),
      );
      return;
    }

    const operations: Array<Promise<unknown>> = [
      ...toCreate.map(r =>
        createApi.mutateAsync(rowToCreatePayload(r, side, orgId, scope)),
      ),
      ...toUpdate.map(r =>
        updateApi.mutateAsync(rowToUpdatePayload(r, side, orgId, scope)),
      ),
      ...toDelete.map(r =>
        deleteApi.mutateAsync({ _id: getObjectId(r.routeId as string) }),
      ),
    ];

    const results = await Promise.allSettled(operations);
    const failed = results.filter(r => r.status === 'rejected').length;
    const succeeded = results.length - failed;

    // Surface the most actionable error from this batch (if any). The
    // backend returns `route_party_not_found` when the counterparty id we
    // sent can't be resolved against `Business` or `Carrier` — today that
    // happens when the user picks from `CustomerSelector` (address book)
    // in the `'direct'` scope. We rewrite the message so the failure mode
    // is legible without diving into devtools.
    if (failed > 0) {
      const firstRejection = results.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected',
      );
      const errId = (firstRejection?.reason as { id?: string } | undefined)?.id;
      if (errId === 'route_party_not_found') {
        toast.error(
          t('sheet.toast.party_not_found', {
            defaultValue:
              "Hero couldn't resolve the selected counterparty. Pick an agreement instead, or contact support to link this customer to a Hero account.",
          }),
        );
        return;
      }
    }

    if (failed === 0) {
      toast.success(
        t('sheet.toast.saved', {
          defaultValue: 'Saved {{count}} rates.',
          count: succeeded,
        }),
      );
    } else if (succeeded === 0) {
      toast.error(
        t('sheet.toast.save_all_failed', {
          defaultValue: 'Failed to save any rates.',
        }),
      );
    } else {
      toast.warning(
        t('sheet.toast.partial_save', {
          defaultValue: 'Saved {{ok}} of {{total}} rates ({{failed}} failed).',
          ok: succeeded,
          total: results.length,
          failed,
        }),
      );
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  // Three mutually-exclusive content states:
  //   1. loading / not-yet-seeded → skeleton
  //   2. seeded with empty result → empty card with CTA
  //   3. rows exist               → table
  //
  // Gating on the store's `hasSeeded` (not just react-query's
  // `isLoading`/`isFetching`) is what makes scope changes feel like a
  // proper list refetch:
  //
  //   - `setScope` / `setSide` flip `hasSeeded` back to `false` and clear
  //     `rows`, so the table can't paint the previous scope's data under
  //     the new scope's header.
  //   - Until `seedFromRoutes()` commits for the new query, `hasSeeded`
  //     stays `false` → skeleton is rendered → no empty-state flash
  //     between "data arrived" and "store reseeded".
  //   - After a save, the api-hook layer invalidates the cache and a
  //     background refetch runs. `hasSeeded` stays `true` throughout
  //     (we don't reset it on mutations), so the table stays visible
  //     instead of blinking back to a skeleton.
  const isInitialLoading = !hasSeeded || (isLoading && rowsLength === 0);

  return (
    <div className="flex flex-col gap-4">
      {redirect ? <PageBack redirect={redirect} /> : null}
      <PricingSheetHeader
        hideScopePicker={hideScopePicker}
        scenarioLabels={scenarioLabels}
      />
      <PricingSheetFilters />
      <PricingSheetBulkBar />

      {isInitialLoading ? (
        <PricingSheetSkeleton />
      ) : rowsLength === 0 ? (
        <PricingSheetEmpty />
      ) : (
        <PricingSheetTable />
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
        <div className="flex items-center gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            onClick={() => addRow()}>
            <PlusIcon className="size-3.5" />
            {t('sheet.footer.add_row', { defaultValue: 'Add row' })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={pendingCount === 0 || isSaving}
            className="h-8 gap-1.5 text-xs text-gray-500"
            onClick={resetChanges}>
            <RefreshCcwIcon className="size-3.5" />
            {t('sheet.footer.reset', { defaultValue: 'Reset changes' })}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <span className="text-[11px] font-medium text-amber-600">
              {t('sheet.footer.pending', {
                defaultValue: '{{count}} pending changes',
                count: pendingCount,
              })}
            </span>
          )}
          <Button
            type="button"
            size="sm"
            className="h-8"
            disabled={savableCount === 0 || isSaving}
            onClick={handleSaveAll}>
            {isSaving
              ? t('sheet.footer.saving', { defaultValue: 'Saving…' })
              : t('sheet.footer.save_all', { defaultValue: 'Save all' })}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// URL persistence model
// =============================================================================
//
// The sheet keeps its session state (scope + side) in two places, by design:
//
//   1. **Zustand store** — runtime source of truth. Every consumer reads from
//      it. Reset to `initialState` on unmount so leaving the pricing tab is
//      a clean wipe (matches the "clear it when I leave" requirement).
//   2. **URL search params** — persistence layer. Survives hard refresh on
//      the pricing tab and makes the view shareable / back-forward-navigable.
//      Wiped on unmount **only if the user is still on the same pathname**
//      (i.e. they switched CRM tabs, not navigated away) so an in-progress
//      `<Link to="/orders">` click doesn't get its params stomped.
//
// Param keys are prefixed with `price` so they can't collide with sibling
// features on the same route — the CRM page already uses `?tab=` and its
// drawers sync `?customerId=`, `?contactId=`, etc.
//
// Hydration ordering is deliberate: we use a `hydratedRef` so the sync
// effect skips its very first run, otherwise the initial render (store
// still on initialState) would race the hydration effect and briefly
// wipe the very params we're about to read.
// =============================================================================

interface UsePricingUrlSyncArgs {
  side: SheetSide;
  scope: ReturnType<typeof usePricingSheetStore.getState>['scope'];
  setSide: (next: SheetSide) => void;
  setScope: ReturnType<typeof usePricingSheetStore.getState>['setScope'];
  resetSheet: () => void;
  /**
   * When true, this hook still mounts (so hooks order stays stable
   * across renders) but skips all URL reads / writes / cleanup. Use on
   * surfaces that own the URL themselves.
   */
  disabled?: boolean;
}

function usePricingUrlSync({
  side,
  scope,
  setSide,
  setScope,
  resetSheet,
  disabled,
}: UsePricingUrlSyncArgs) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hydratedRef = useRef(false);

  // --- Hydrate from URL once on mount ---------------------------------------
  // Customer / agreement are seeded with empty `name` / `label` — the header
  // pickers (`CustomerSelector`, `AgreementScopePicker`) fetch the real
  // entities by id and either render the resolved name or, in the agreement
  // case, re-commit the scope via the existing resolve-and-derive effect.
  useEffect(() => {
    if (disabled) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const urlSide = searchParams.get(URL_PARAM.side);
    if (urlSide === 'buy' || urlSide === 'sell') {
      setSide(urlSide);
    }

    const customerId = searchParams.get(URL_PARAM.customer);
    const agreementId = searchParams.get(URL_PARAM.agreement);

    if (customerId && agreementId) {
      setScope({
        kind: 'agreement',
        agreement: { id: agreementId, label: '' },
        counterparty: { id: customerId, name: '' },
      });
    } else if (customerId) {
      setScope({
        kind: 'direct',
        counterparty: { id: customerId, name: '' },
      });
    }
    // Hydration is mount-only. Subsequent URL→store flow would create a loop
    // with the sync effect below; the store is the runtime source of truth
    // from this point on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Sync store → URL on every change -------------------------------------
  // `replace: true` keeps each pick out of browser history — the user should
  // be able to walk through actual page visits with back/forward, not every
  // customer they auditioned.
  useEffect(() => {
    if (disabled) return;
    if (!hydratedRef.current) return;
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        // Side: omit the default ('sell') so the common-case URL stays clean.
        if (side === 'sell') next.delete(URL_PARAM.side);
        else next.set(URL_PARAM.side, side);

        if (scope.kind === 'own') {
          next.delete(URL_PARAM.customer);
          next.delete(URL_PARAM.agreement);
        } else if (scope.kind === 'direct') {
          next.set(URL_PARAM.customer, scope.counterparty.id);
          next.delete(URL_PARAM.agreement);
        } else {
          next.set(URL_PARAM.customer, scope.counterparty.id);
          next.set(URL_PARAM.agreement, scope.agreement.id);
        }
        return next;
      },
      { replace: true },
    );
  }, [side, scope, setSearchParams, disabled]);

  // --- Cleanup on unmount ---------------------------------------------------
  // Reset the in-memory store unconditionally (the page is gone; whatever
  // mounts next deserves a clean slate). Wipe the URL params only if we're
  // still on the same pathname — if the user clicked a link to /orders,
  // the new route is already in charge of the URL and we shouldn't fight it.
  //
  // CRITICAL: do *not* use a functional updater (`prev => ...`) here. In
  // react-router v7, `useSearchParams` returns a `setSearchParams` whose
  // functional-updater `prev` is the `searchParams` captured in its
  // `useCallback` closure (deps include `searchParams`), not the live URL.
  // The cleanup runs with the mount-time setter (no re-render happens
  // before unmount, since the parent's new render omits this subtree), so
  // `prev` would be the *mount-time* URL (e.g. `?tab=pricing`). Writing
  // that back would clobber whatever tab change the user just made on their
  // way out (clicking "Saved locations" would visibly bounce back to
  // "Pricing"). Instead, read the live URL from `window.location.search`
  // and pass a concrete `URLSearchParams` value — that path skips the
  // stale-closure read entirely.
  useEffect(() => {
    if (disabled) return undefined;
    const mountPath =
      typeof window !== 'undefined' ? window.location.pathname : '';
    return () => {
      resetSheet();
      if (
        typeof window === 'undefined' ||
        window.location.pathname !== mountPath
      ) {
        return;
      }
      const next = new URLSearchParams(window.location.search);
      next.delete(URL_PARAM.side);
      next.delete(URL_PARAM.customer);
      next.delete(URL_PARAM.agreement);
      setSearchParams(next, { replace: true });
    };
    // Empty deps on purpose: mountPath captures-at-mount is the whole point
    // (we want to know where we *started*, not where we are now). The
    // setter's stale closure is sidestepped by passing a concrete value
    // built from `window.location.search` instead of using `prev => ...`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
